/**
 * API Interceptors Setup
 *
 * This file configures Axios interceptors to:
 * 1. Add authentication tokens to outgoing requests
 * 2. Handle authentication errors (401/403) by logging out the user
 * 3. Preserve the current URL for redirecting back after re-authentication
 *
 * The interceptors enhance the base Axios instance from api.js
 */

import authConfig from "../config/auth";
import { logout } from "../store/slices/auth";
import axiosInstance from "./api";

/**
 * Setup function to configure Axios interceptors with Redux store access
 *
 * @param {Object} store - The Redux store instance for dispatching actions
 */
const setup = (store) => {
  // Request interceptor - adds auth token to all outgoing requests
  axiosInstance.interceptors.request.use(
    (config) => {
      // Get auth token from localStorage
      const storedToken = localStorage.getItem(authConfig.storageTokenKeyName);
      const token = storedToken && storedToken;

      // Add token to request headers if available
      if (token) {
        config.headers["authorization"] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      // Handle request errors
      return Promise.reject(error);
    }
  );

  // Get dispatch function from Redux store
  const { dispatch } = store;

  // Response interceptor - handles auth errors and other response processing
  axiosInstance.interceptors.response.use(
    (res) => {
      // Pass through successful responses
      return res;
    },
    async (error) => {
      if (error.response) {
        const { status } = error.response;

        // Handle authentication errors (except during login attempts)
        if (
          (status === 401 || status === 403) &&
          error.config.url !== `/${authConfig.loginEndpoint}`
        ) {
          // Log out the user and store current path for post-login redirect
          dispatch(
            logout({
              value: `${window.location.pathname}${window.location.search}`,
              type: "redirect"
            })
          );
          return Promise.reject(error);
        } else {
          // Pass through other errors
          return Promise.reject(error);
        }
      } else {
        // Handle network errors or other non-response errors
        return Promise.reject(error);
      }
    }
  );
};

export default setup;
